import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { DateRangeFilter } from 'components/DateRangeFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import type { CatalogRatioData } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import { formatDateToLocale, formatNumberToRu } from 'utils';

import type { ColDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';

import type { RatioIdType } from '../../types';
import Styles from '../catalogRatios.module.css';
import {
    catalogRatiosCodeFilterGql,
    catalogRatiosGroupFilterGql,
    catalogRatiosNameFilterGql,
    catalogRatiosTypeFilterGql,
} from '../catalogRatiosFilterQueries';
import { ColumnPairs, RatiosTypeEnum } from '../catalogRatiosHelpers';
import { TableRowActionsButton } from '../components';

type TUseCatalogRatiosColumnsProps = {
    catalogRatiosRules: IUseAccess['catalogRatios'];
    onEdit: (id: RatioIdType) => () => void;
    onRemove: (id: Exclude<RatioIdType, undefined>) => () => void;
};

export const useCatalogRatiosColumns = ({ catalogRatiosRules, onEdit, onRemove }: TUseCatalogRatiosColumnsProps) => {
    const isRowActionsDisabled = useMemo(
        () => !catalogRatiosRules.canEdit || !catalogRatiosRules.canDelete,
        [catalogRatiosRules],
    );

    const columns = useMemo<ColDef<CatalogRatioData>[]>(
        () => [
            {
                field: 'rowActions' as any,
                headerName: '',
                pinned: true,
                cellRenderer: ({ data }: { data: CatalogRatioData }) => (
                    <TableRowActionsButton
                        id={data?.id}
                        onEdit={onEdit}
                        onRemove={onRemove}
                        catalogRatiosRules={catalogRatiosRules}
                        disabled={isRowActionsDisabled}
                    />
                ),
                width: 48,
            },

            {
                field: 'updatedAt',
                headerName: ColumnPairs.updatedAt,
                flex: 1,
                minWidth: 175,
                cellRenderer: ({ data }: { data: CatalogRatioData }) => formatDateToLocale(data?.updatedAt),
                filter: DateRangeFilter,
            },
            {
                field: 'catalogRatioGroupName',
                headerName: ColumnPairs.groupName,
                flex: 1,
                minWidth: 200,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    dataAccessor: 'catalogRatioGroupFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: ColumnPairs.groupName,
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: catalogRatiosGroupFilterGql,
                },
            },
            {
                field: 'code',
                headerName: ColumnPairs.code,
                flex: 1,
                minWidth: 160,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    dataAccessor: 'catalogRatioCodeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: ColumnPairs.code,
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: catalogRatiosCodeFilterGql,
                },
            },
            {
                field: 'name',
                headerName: ColumnPairs.name,
                flex: 2,
                minWidth: 400,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    dataAccessor: 'catalogRatioNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: ColumnPairs.name,
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: catalogRatiosNameFilterGql,
                },
            },
            {
                field: 'ratioType',
                headerName: ColumnPairs.ratioType,
                flex: 1,
                minWidth: 190,
                valueFormatter: ({ value }: { value: keyof typeof RatiosTypeEnum }) => RatiosTypeEnum[value],
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    withSearch: false,
                    dataAccessor: 'catalogRatioTypeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: ColumnPairs.ratioType,
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueFormatter: ({ value }: { value: keyof typeof RatiosTypeEnum }) =>
                                RatiosTypeEnum[value],
                        },
                    ],
                    query: catalogRatiosTypeFilterGql,
                },
            },
            {
                field: 'ratios' as any,
                headerName: ColumnPairs.ratios,
                children: [
                    {
                        field: 'kozp',
                        headerName: ColumnPairs.kozp,
                        flex: 1,
                        minWidth: 152,
                        cellRenderer: ({ value }: { value: string }) => (
                            <div className={Styles.ratioCell}>{formatNumberToRu(value, 4) ?? ''}</div>
                        ),
                    },
                    {
                        field: 'kem',
                        headerName: ColumnPairs.kem,
                        flex: 1,
                        minWidth: 152,
                        cellRenderer: ({ value }: { value: string }) => (
                            <div className={Styles.ratioCell}>{formatNumberToRu(value, 4) ?? ''}</div>
                        ),
                    },
                    {
                        field: 'knr',
                        headerName: ColumnPairs.knr,
                        flex: 1,
                        minWidth: 152,
                        cellRenderer: ({ value }: { value: string }) => (
                            <div className={Styles.ratioCell}>{formatNumberToRu(value, 4) ?? ''}</div>
                        ),
                    },
                    {
                        field: 'ksp',
                        headerName: ColumnPairs.ksp,
                        flex: 1,
                        minWidth: 152,
                        cellRenderer: ({ value }: { value: string }) => (
                            <div className={Styles.ratioCell}>{formatNumberToRu(value, 4) ?? ''}</div>
                        ),
                    },
                    {
                        field: 'kmat',
                        headerName: ColumnPairs.kmat,
                        flex: 1,
                        minWidth: 152,
                        cellRenderer: ({ value }: { value: string }) => (
                            <div className={Styles.ratioCell}>{formatNumberToRu(value, 4) ?? ''}</div>
                        ),
                    },
                    {
                        field: 'ktz',
                        headerName: ColumnPairs.ktz,
                        flex: 1,
                        minWidth: 152,
                        cellRenderer: ({ value }: { value: string }) => (
                            <div className={Styles.ratioCell}>{formatNumberToRu(value, 4) ?? ''}</div>
                        ),
                    },
                    {
                        field: 'ktzzpm',
                        headerName: ColumnPairs.ktzzpm,
                        flex: 1,
                        minWidth: 152,
                        cellRenderer: ({ value }: { value: string }) => (
                            <div className={Styles.ratioCell}>{formatNumberToRu(value, 4) ?? ''}</div>
                        ),
                    },
                ],
            },
        ],
        [catalogRatiosRules, onEdit, onRemove, isRowActionsDisabled],
    );

    const [selection, , , setSelection] = useSelection('catalogRatios');
    const [pinnedItems, , , setPinned] = usePin('catalogRatios');

    const fullSetSettingsColumns = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                const newColumn = { ...column } as ColDef & { disabled: boolean };

                if (newColumn.field === 'rowActions') {
                    return acc;
                }

                if (['code', 'name']?.includes(newColumn.field ?? '')) {
                    newColumn.disabled = true;
                }

                if (pinnedItems[getColumnId(column)] || newColumn.field === 'rowActions') {
                    newColumn.pinned = true;
                }

                acc.push(newColumn);

                return acc;
            }, []),
        [columns, pinnedItems],
    );

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const tableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
                setSelection,
                setPinned,
            });
        },
        [fullSetSettingsColumns, setSelection, setPinned],
    );

    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    return { columns: fullSetColumns, fullSetSettingsColumns, isColumnVisible, tableSettingsConfirm };
};
